import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../../utils/api';

/**
 * Custom hook for managing families data fetching and state
 * Uses hierarchical endpoint: /api/products?projectId={id}&includeFeatures=true
 */
export function useFamiliesData() {
    // Raw Data States
    const [families, setFamilies] = useState([]);
    const [projects, setProjects] = useState([]);
    const [rulesets, setRulesets] = useState([]);
    const [features, setFeatures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch all required data from backend
    const fetchData = useCallback(async (options = {}) => {
        setLoading(true);
        setError(null);
        try {
            // Load projects
            const projRes = await apiFetch(`/api/projects`);
            const projData = projRes.ok ? await projRes.json() : [];
            setProjects(projData);
            
            // Load families with nested rulesets + features
            const currentProjId = localStorage.getItem('activeProjectId') || '';
            const url = currentProjId ? `/api/products?projectId=${currentProjId}&includeFeatures=true` : '/api/products';
            const famRes = await apiFetch(url);
            if (!famRes.ok) {
                throw new Error('Failed to load families');
            }
            const famData = await famRes.json();
            setFamilies(famData);
            
            // Extract and deduplicate rulesets and features from families
            // For many-to-many entities, track which families they belong to
            const featuresMap = new Map();
            const rulesetsMap = new Map();
            
            famData.forEach(f => {
                // Track feature-family relationships
                (f.features || []).forEach(feat => {
                    if (!featuresMap.has(feat.id)) {
                        featuresMap.set(feat.id, { ...feat, sourceFamilyIds: [] });
                    }
                    const existing = featuresMap.get(feat.id);
                    if (!existing.sourceFamilyIds.includes(f.id)) {
                        existing.sourceFamilyIds.push(f.id);
                    }
                });
                
                // Track ruleset-family relationships
                (f.ruleSets || []).forEach(rs => {
                    if (!rulesetsMap.has(rs.id)) {
                        rulesetsMap.set(rs.id, { ...rs, sourceFamilyIds: [] });
                    }
                    const existing = rulesetsMap.get(rs.id);
                    if (!existing.sourceFamilyIds.includes(f.id)) {
                        existing.sourceFamilyIds.push(f.id);
                    }
                });
            });
            
            const allRulesets = Array.from(rulesetsMap.values());
            const allFeatures = Array.from(featuresMap.values());
            
            setRulesets(allRulesets);
            setFeatures(allFeatures);
        } catch (err) {
            console.error('Failed to load families page data', err);
            setError(err);
        } finally {
            setLoading(false);
        }
    }, []);

    // Initial data load
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Re-fetch when the header project silo changes (create, switch, import)
    useEffect(() => {
        const handleGlobalProjectChange = () => {
            fetchData();
        };
        window.addEventListener('activeProjectChanged', handleGlobalProjectChange);
        return () => window.removeEventListener('activeProjectChanged', handleGlobalProjectChange);
    }, [fetchData]);

    // Re-fetch when version changes
    useEffect(() => {
        const handleGlobalVersionChange = () => {
            fetchData();
        };
        window.addEventListener('activeVersionChanged', handleGlobalVersionChange);
        return () => window.removeEventListener('activeVersionChanged', handleGlobalVersionChange);
    }, [fetchData]);

    return {
        families,
        projects,
        rulesets,
        features,
        loading,
        error,
        fetchData,
        setFamilies,
        setProjects,
        setRulesets,
        setFeatures,
        setLoading
    };
}
